import PropTypes from "prop-types";
import { Search, XCircle } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { InvestmentListRow } from "components/investments/InvestmentListRow";
import {
  getInvestmentRecordId,
  INVESTMENT_STATUS_TABS,
} from "lib/investmentListHelpers";

const TAB_VALUES = INVESTMENT_STATUS_TABS.map((t) => t.value);

function InvestmentsTabPanel({ loading, investments, tab, emptyMessage, onRequestCancel }) {
  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading investments...
      </div>
    );
  }

  if (investments.length === 0) {
    return (
      <p className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {investments.map((investment) => (
        <InvestmentListRow
          key={getInvestmentRecordId(investment) || `${tab}-${investment.createdAt}`}
          investment={investment}
          tab={tab}
          onRequestCancel={onRequestCancel}
        />
      ))}
    </div>
  );
}

InvestmentsTabPanel.propTypes = {
  loading: PropTypes.bool.isRequired,
  investments: PropTypes.arrayOf(PropTypes.object).isRequired,
  tab: PropTypes.oneOf(TAB_VALUES).isRequired,
  emptyMessage: PropTypes.string.isRequired,
  onRequestCancel: PropTypes.func,
};

InvestmentsTabPanel.defaultProps = {
  onRequestCancel: undefined,
};

const TAB_EMPTY_MESSAGES = {
  all: "You have not made any investments yet.",
  pending: "No pending investments.",
  paid: "No paid investments.",
  failed: "No failed investments.",
  cancelled: "No cancelled investments.",
};

export function MyInvestmentsList({
  loading,
  error,
  investments,
  tab,
  onTabChange,
  onRetry,
  searchDraft,
  onSearchDraftChange,
  onSearchEnter,
  onClearSearch,
  onClearFilters,
  hasActiveFilters,
  onRequestCancel,
}) {
  return (
    <Tabs value={tab} onValueChange={onTabChange}>
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <TabsList className="h-auto w-full flex-wrap justify-start gap-1 lg:w-auto">
          {INVESTMENT_STATUS_TABS.map(({ value, label }) => (
            <TabsTrigger key={value} value={value}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex w-full shrink-0 items-center gap-2 lg:w-auto lg:max-w-sm">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchDraft}
              onChange={(e) => onSearchDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSearchEnter();
              }}
              placeholder="Search investments..."
              className="pl-10"
              aria-label="Search investments"
            />
          </div>
          {searchDraft.trim() ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={onClearSearch}
              aria-label="Clear search"
            >
              <XCircle className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <div className="flex flex-wrap gap-2">
            {hasActiveFilters ? (
              <Button type="button" variant="outline" size="sm" onClick={onClearFilters}>
                Clear filters
              </Button>
            ) : null}
            <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={loading}>
              {loading ? "Retrying..." : "Retry"}
            </Button>
          </div>
        </div>
      ) : null}

      <TabsContent value={tab} className="space-y-4 mt-0">
        {error ? null : (
          <InvestmentsTabPanel
            loading={loading}
            investments={investments}
            tab={tab}
            emptyMessage={TAB_EMPTY_MESSAGES[tab] ?? TAB_EMPTY_MESSAGES.all}
            onRequestCancel={onRequestCancel}
          />
        )}
      </TabsContent>
    </Tabs>
  );
}

MyInvestmentsList.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  investments: PropTypes.arrayOf(PropTypes.object).isRequired,
  tab: PropTypes.oneOf(TAB_VALUES).isRequired,
  onTabChange: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  searchDraft: PropTypes.string.isRequired,
  onSearchDraftChange: PropTypes.func.isRequired,
  onSearchEnter: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  onRequestCancel: PropTypes.func,
};

MyInvestmentsList.defaultProps = {
  error: null,
  onRequestCancel: undefined,
};

